/**
 * Building a logged meal from components.
 *
 * A meal you log is assembled the way every meal in this plan is: from an ingredient
 * list, with the totals computed rather than asserted (section 3). Each component is
 * either a food looked up from Open Food Facts, scaled to the grams you actually ate,
 * or a set of macros you typed in directly.
 *
 * Open Food Facts is community-contributed and its data is frequently wrong, so nothing
 * from it is trusted without checking. `plausibility()` is the backstop.
 */
import type { PlanState } from './types.ts';
import { kcalFromMacros } from './nutrition.ts';
import type { Macros } from './nutrition.ts';

/** Macros per 100g, as Open Food Facts stores them. */
export interface Per100 { k: number; p: number; f: number; c: number }

export interface FoodResult {
  code: string;
  name: string;
  brand: string;
  servingSize: string;
  per100: Per100 | null;
}

/** One line of a logged meal. */
export interface MealComponent {
  n: string;
  /** Grams eaten, when the component came from a per-100g source. */
  g: number | null;
  per100: Per100 | null;
  code: string | null;
  /** The component's contribution, already scaled. */
  k: number; p: number; f: number; c: number;
}

export const foodCacheKey = (code: string): string => `foodcache:${code}`;

/** Scale a per-100g food to the portion actually eaten. Calories are recomputed from
 *  the scaled macros rather than scaled from the stated figure, so the meal total stays
 *  internally consistent even when the source's own calorie number does not. */
export function scaleTo(per100: Per100, grams: number): Macros {
  const r = grams / 100;
  const p = round(per100.p * r), f = round(per100.f * r), c = round(per100.c * r);
  return { k: kcalFromMacros(p, f, c), p, f, c };
}

export function componentFromFood(food: FoodResult, grams: number): MealComponent | null {
  if (!food.per100 || !Number.isFinite(grams) || grams <= 0) return null;
  const m = scaleTo(food.per100, grams);
  const label = [food.name, food.brand].filter(Boolean).join(', ');
  return { n: label || food.name, g: grams, per100: food.per100,
           code: food.code || null, ...m };
}

export function componentFromMacros(
  name: string, p: number, f: number, c: number,
): MealComponent | null {
  if (!name.trim()) return null;
  if (![p, f, c].every((v) => Number.isFinite(v) && v >= 0)) return null;
  return { n: name.trim(), g: null, per100: null, code: null,
           k: kcalFromMacros(p, f, c), p, f, c };
}

/** Meal totals, summed from the components. Never asserted. */
export function totalOf(items: readonly MealComponent[]): Macros {
  const t = items.reduce((a, i) => ({ p: a.p + i.p, f: a.f + i.f, c: a.c + i.c }),
    { p: 0, f: 0, c: 0 });
  const p = round(t.p), f = round(t.f), c = round(t.c);
  return { k: kcalFromMacros(p, f, c), p, f, c };
}

// ------------------------------------------------------------------ plausibility

export interface Warning { severity: 'block' | 'warn'; text: string }

export const ATWATER_TOLERANCE = 0.20;
export const MAX_MEAL_KCAL = 3000;
export const MAX_MEAL_PROTEIN_G = 250;

/**
 * Checks a per-100g figure before it is trusted. Open Food Facts entries are typed in
 * by members of the public and a wrong one silently moves every calorie total downstream.
 */
export function plausibility(per100: Per100 | null): Warning[] {
  if (!per100) {
    return [{ severity: 'block',
      text: 'This product has no protein, fat and carbohydrate figures, so it cannot be used.' }];
  }
  const out: Warning[] = [];
  const mass = per100.p + per100.f + per100.c;
  if (mass > 100) {
    out.push({ severity: 'block',
      text: `Its macros add up to ${round(mass)}g per 100g, which is impossible. `
        + 'The entry is wrong.' });
  }
  const computed = kcalFromMacros(per100.p, per100.f, per100.c);
  if (computed > 0) {
    const drift = Math.abs(per100.k - computed) / computed;
    if (drift > ATWATER_TOLERANCE) {
      out.push({ severity: 'warn',
        text: `It claims ${round(per100.k)} kcal per 100g but its macros come to `
          + `${computed}. Open Food Facts entries are contributed by the public and this `
          + 'one does not add up. The meal will use the figure from the macros.' });
    }
  }
  if (per100.p > 100 || per100.f > 100 || per100.c > 100) {
    out.push({ severity: 'block', text: 'A figure over 100g per 100g is not possible.' });
  }
  return out;
}

/** A last look at the assembled meal, to catch a mistyped portion. */
export function mealPlausibility(total: Macros): Warning[] {
  const out: Warning[] = [];
  if (total.k > MAX_MEAL_KCAL) {
    out.push({ severity: 'warn',
      text: `That is ${Math.round(total.k)} kcal in a single meal. Check the portion sizes.` });
  }
  if (total.p > MAX_MEAL_PROTEIN_G) {
    out.push({ severity: 'warn',
      text: `That is ${Math.round(total.p)}g of protein in one meal. Check the portion sizes.` });
  }
  return out;
}

export function usable(per100: Per100 | null): boolean {
  return plausibility(per100).every((w) => w.severity !== 'block');
}

// ------------------------------------------------------------------ offline cache

/** Foods you have used before, kept so they work with no signal. */
export function cachedFoods(state: PlanState): FoodResult[] {
  return state.entries('foodcache:')
    .map(([, v]) => v as FoodResult)
    .filter((f) => f && typeof f.name === 'string' && f.name !== '' && usable(f.per100))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function round(n: number): number { return Math.round(n * 10) / 10; }
